import * as Sentry from '@sentry/react';
import { FirestoreError } from 'firebase/firestore';

const NETWORK_ERROR_PATTERN =
  /network|offline|transport|failed to fetch|load failed|cancelled|net::ERR_|ECONNABORTED|Connection to Indexed Database server lost/i;

const FIRESTORE_NETWORK_CODES = new Set(['unavailable', 'deadline-exceeded']);

export function isNetworkError(err: unknown): boolean {
  if (err instanceof FirestoreError && FIRESTORE_NETWORK_CODES.has(err.code)) return true;
  if (err instanceof TypeError && NETWORK_ERROR_PATTERN.test(err.message)) return true;
  if (err instanceof DOMException && err.name === 'AbortError') return true;
  if (err instanceof Error && NETWORK_ERROR_PATTERN.test(err.message)) return true;
  return false;
}

/**
 * HTML5 media `play()` rejects with an AbortError when the request is
 * interrupted before playback begins — either by a `pause()` call or by the
 * media element being removed from the document (e.g. rapid play/pause toggles
 * or navigating away mid-play). These are benign races and should not be
 * surfaced to the user or reported to Sentry.
 */
export function isMediaPlayAbortError(err: unknown): boolean {
  return err instanceof DOMException && err.name === 'AbortError';
}

/**
 * Calls `play()` on a media element while swallowing the benign AbortError that
 * occurs when the play request is interrupted. Any other rejection (e.g. an
 * autoplay-policy or format error) is still reported to Sentry.
 */
export function safePlay(media: HTMLMediaElement | null | undefined): void {
  const promise = media?.play();
  if (promise) {
    promise.catch((err: unknown) => {
      if (!isMediaPlayAbortError(err)) Sentry.captureException(err);
    });
  }
}

const MEDIA_ERR_ABORTED = 1; // MediaError.MEDIA_ERR_ABORTED (the global is absent in jsdom)
const MEDIA_ERROR_NAMES: Record<number, string> = {
  1: 'MEDIA_ERR_ABORTED',
  2: 'MEDIA_ERR_NETWORK',
  3: 'MEDIA_ERR_DECODE',
  4: 'MEDIA_ERR_SRC_NOT_SUPPORTED',
};

/**
 * A media element failure, carrying what the bare `error` Event hides: the
 * MediaError code (2 = network, 3 = decode, 4 = format/404) and the source URL.
 */
export class MediaPlaybackError extends Error {
  constructor(readonly code: number, readonly src: string, detail?: string) {
    const name = MEDIA_ERROR_NAMES[code] ?? `MEDIA_ERR_UNKNOWN(${code})`;
    super(`${name}${detail ? `: ${detail}` : ''} — ${src || '(no src)'}`);
    this.name = 'MediaPlaybackError';
  }
}

/**
 * `<video>`/`<audio>` report failures as a bare `error` Event on the element.
 * Captured as-is, Sentry can only say "Event `Event` (type=error)" (CUSTOM-APP-7:
 * 63 events with no way to tell a dead file from a lost connection). Translate
 * such an event into a MediaPlaybackError with the code and source; return null
 * for MEDIA_ERR_ABORTED (the load was abandoned, e.g. by navigating away — not
 * a defect). Anything that isn't a media error event comes back unchanged.
 */
export function describeMediaError(err: unknown): unknown {
  if (!(err instanceof Event) || !(err.target instanceof HTMLMediaElement)) return err;
  const media = err.target;
  const code = media.error?.code ?? 0;
  if (code === MEDIA_ERR_ABORTED) return null;
  return new MediaPlaybackError(code, media.currentSrc || media.src, media.error?.message);
}

export const handleError = (error: unknown): void => {
  Sentry.captureException(error);
};

const CHUNK_RELOAD_KEY = 'xsite:chunk-reload-at';
const CHUNK_RELOAD_COOLDOWN_MS = 60_000;

/**
 * A dynamic import() — a lazy() route or a player chunk — failed to load:
 * either a network blip or a page still pointing at chunks a newer deploy has
 * removed (Safari: "Importing a module script failed.", CUSTOM-APP-2E). A
 * browser never re-fetches a module URL that failed within the same document,
 * so the only recovery is a reload, which also picks up the current index.html.
 * Reloads at most once a minute so a dead network cannot loop the page; returns
 * whether a reload was triggered.
 */
export const reloadOnChunkLoadError = (): boolean => {
  let lastReload = 0;
  try {
    lastReload = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY)) || 0;
  } catch { /* storage unavailable — reload anyway */ }
  if (Date.now() - lastReload < CHUNK_RELOAD_COOLDOWN_MS) return false;
  try {
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
  } catch { /* storage unavailable — reload anyway */ }
  window.location.reload();
  return true;
};

export const setupGlobalErrorHandlers = (): void => {
  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    if (event.reason == null) return;
    Sentry.captureException(event.reason);
  });

  window.addEventListener('error', (event: ErrorEvent) => {
    if (event.error == null) return;
    Sentry.captureException(event.error);
  });

  // Vite dispatches this when a dynamic import() fails, before rejecting it.
  window.addEventListener('vite:preloadError', () => {
    reloadOnChunkLoadError();
  });
};

export const withErrorHandling = async <T>(apiCall: () => Promise<T>, errorMessage: string = 'An error occurred'): Promise<T> => {
  try {
    return await apiCall();
  } catch (error: unknown) {
    Sentry.captureException(error, {
      extra: { errorMessage },
    });
    throw new Error(errorMessage);
  }
};
